import { Alert, Autocomplete, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, Tooltip, Typography } from '@mui/material';
import { useState, useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import { SkeletonDataGrid } from '../../../../reusable-components/skeleton/SkeletonDataGrid';
import { filterRows, getGridFilterFieldValues, GetGridKeys, loadSavedFilters, loadSavedSorts, onSortModelChange } from '../../../../reusable-components/datagrid/dataGridHelper';
import { CustomFilter, createColumns } from '../../../../reusable-components/datagrid/custom-filters';
import { INACTIVE_PRODUCTS } from '../../../../reusable-components/datagrid/orderGuidesColumns';
import { useFilterModel } from '../../../../hooks/useFilterModel';
import DataGridPersistSettings from '../../../../reusable-components/datagrid/DataGridPersistSettings';
import { CustomSettings } from '../../../../reusable-components/datagrid/grid-settings';
import { removeUserSettings } from '../../../../redux/slices/user';
import { addAdditionalCategories } from '../../../../api/catalog';
import { useSnackbar } from '../../../../reusable-components/snackbar';

OrderGuideCatalogGrid.propTypes = {
  currentOrderGuide: PropTypes.object,
  data: PropTypes.object,
  tempOrderGuide: PropTypes.array,
  setTempOrderGuide: PropTypes.func,
  handleUpdateOrderGuide: PropTypes.func,
  quickFilter: PropTypes.string,
  subcategories: PropTypes.array,
};
export default function OrderGuideCatalogGrid({ currentOrderGuide, data, tempOrderGuide, setTempOrderGuide, handleUpdateOrderGuide, quickFilter, subcategories }) {
  
  const guideTitleStyles = {
    color: 'secondary.main',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const { data: { categories, orderGuide }, isLoading, isLoadingProducts } = useSelector((state) => state.orderGuides);
  const guideCategoryName = currentOrderGuide?.agoraCategory ?? categories.find(x => x.value === currentOrderGuide?.agoraCategoryId)?.label;
  const gridId = 'order-guide-catalog-items';

  const gridRef = useGridApiRef();
  const { filterStorageKey, sortStorageKey, gridKey, baseSearchTextStorageKey } = GetGridKeys(gridId);

  const [isSelectingCatalog, setIsSelectingCatalog] = useState(false);
  const [selectedRowIdsCatalog, setSelectedRowIdsCatalog] = useState([]);
  const [isCatalogExpanded, setIsCatalogExpanded] = useState(false);
  const [filters, setFilters] = useState(loadSavedFilters(filterStorageKey));
  const filterModel = useFilterModel(filters);
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeFilter, setActiveFilter] = useState(null);
  const [sortModel, setSortModel] = useState(loadSavedSorts(sortStorageKey));
  const [columnsState, setColumnsState] = useState();
  const dispatch = useDispatch();
  const [pendingRows, setPendingRows] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const columnReorder = (colState) => {
    try {
      const updatedColumns = colState?.columns
        ? INACTIVE_PRODUCTS
          .map((col) => ({ ...col, ...colState?.columns?.dimensions[col.field] }))
          .sort((a, b) => {
            const indexA = colState?.columns?.orderedFields?.indexOf(a.field);
            const indexB = colState?.columns?.orderedFields?.indexOf(b.field);
            if (indexA > -1 && indexB > -1) return indexA - indexB;
            return 0;
          })
        : INACTIVE_PRODUCTS;
      return updatedColumns;
    } catch (err) {
      console.log(err);
      return INACTIVE_PRODUCTS;
    }
  };
  const inactiveColumns = useMemo(() => createColumns(columnReorder(columnsState), filters, setAnchorEl, setActiveFilter, gridId, activeFilter),
    [activeFilter, filters, gridId, columnsState]);
  
  const {gridPersists, gridSettings, updateGridSettings } = DataGridPersistSettings({ gridId, columns: inactiveColumns });  

  const initialState = useMemo(() => gridSettings?.gridState || {}, [gridSettings]);


  const handleSelectClickCatalog = useCallback(() => {
    setSelectedRowIdsCatalog([]);
    setIsSelectingCatalog((prev) => !prev);
  }, []);

  const handleSelectionModelChangeCatalog = (selection) => {
    setSelectedRowIdsCatalog(selection);
  };

  const dataByCategory = useMemo(() => data.unactiveData.filter((item) =>
    !item.deactivated &&
    !tempOrderGuide?.find((og) => og.productId === item.id && og.deleteFlag === false)
  ), [data.unactiveData, tempOrderGuide]);

  const filteredRows = useMemo(() => filterRows(dataByCategory, quickFilter, inactiveColumns), [dataByCategory, quickFilter, inactiveColumns]);

  const fieldValues = useMemo(() => getGridFilterFieldValues(inactiveColumns, filteredRows, filters), [filteredRows, filters, inactiveColumns]);

  const addRowsToOrderGuide = useCallback(async (selectedRows) => {
    const newItems = selectedRows
      .filter((row) => !tempOrderGuide.some((item) => item.productId === row.id && !item.deleteFlag))
      .map((row) => ({
        deleteFlag: false,
        productId: row.id,
        itemId: row.productDetails[0]?.id,
      }));
    const newOrderGuide = [...tempOrderGuide, ...newItems];
    const updated = await handleUpdateOrderGuide(newOrderGuide);
    if (!updated) return false;
    setTempOrderGuide(newOrderGuide);
    handleSelectClickCatalog();
    return true;
  }, [tempOrderGuide, handleUpdateOrderGuide, handleSelectClickCatalog, setTempOrderGuide]);

  const handleAddSelectedRows = useCallback(() => {
    const selectedRows = dataByCategory.filter((row) => selectedRowIdsCatalog.includes(row.id));
    const rows = selectedRows.map(row => ({
      row,
      needsCategory: row.agoraCategory?.id !== currentOrderGuide?.agoraCategoryId &&
        !row.extraCategories?.some(category => category.agoraCategoryId === currentOrderGuide?.agoraCategoryId),
      category: row.agoraCategory,
      subcategory: row.agoraSubcategory,
    }));
    if (!rows.some(row => row.needsCategory)) {
      addRowsToOrderGuide(selectedRows);
      return;
    }
    setPendingRows(rows);
  }, [addRowsToOrderGuide, currentOrderGuide?.agoraCategoryId, dataByCategory, selectedRowIdsCatalog]);

  const updateCategorySelection = (productId, changes) => {
    setPendingRows(current => current.map(item => item.row.id === productId ? { ...item, ...changes } : item));
  };

  const handleConfirmAdd = async () => {
    if (!currentOrderGuide) return;
    const items = pendingRows
      .filter(item => item.needsCategory && item.category?.id === currentOrderGuide.agoraCategoryId)
      .map(item => ({ productId: item.row.id, agoraSubcategoryId: item.subcategory?.id }));
    if (items.some((item) => !item.agoraSubcategoryId)) return;
    setIsAdding(true);
    try {
      if (items.length) {
        const response = await addAdditionalCategories(items, currentOrderGuide.agoraCategoryId);
        if (!response.data) throw new Error('Unable to add categories');
      }
      const updated = await addRowsToOrderGuide(pendingRows.map(item => item.row));
      if (updated) setPendingRows([]);
    } catch (error) {
      enqueueSnackbar(typeof error === 'string' ? error : 'Unable to add categories', { variant: 'error' });
    } finally {
      setIsAdding(false);
    }
  };

    useEffect(() => {
        const currentState = gridRef?.current?.exportState();
        setColumnsState(currentState);
    }, [gridRef]);

    const columnVisibilityModel = columnsState?.columns?.columnVisibilityModel ?? initialState.columns?.columnVisibilityModel ?? {};

    const handleGridVisibilityChange = (model) => {
        const newState = { ...columnsState, columns: { ...columnsState?.columns, columnVisibilityModel: model } };
        setColumnsState(newState);
        if (gridPersists) updateGridSettings(newState);
    };
  
    const handleGridOrderChange = () => {
        const currentState = gridRef?.current?.exportState();
        if (gridPersists) updateGridSettings(currentState);
        setColumnsState(currentState);
    };

    const setColumnWidths = () => {
      const currentState = gridRef?.current?.exportState();
      if (gridPersists) updateGridSettings(currentState);
      setColumnsState(currentState);
    };

    const resetGridState = () => {
        // remove settings from database and localstorage
        updateGridSettings.cancel();
        localStorage.removeItem(gridKey);
        localStorage.removeItem(filterStorageKey);
        localStorage.removeItem(sortStorageKey);
        columnsState?.columns?.orderedFields?.forEach((c) => localStorage.removeItem(`${baseSearchTextStorageKey}-${c}`));
        setFilters({});
        setSortModel([]);
        setColumnsState({
          columns: {
            columnVisibilityModel: {},
            dimensions: {},
            orderedFields: INACTIVE_PRODUCTS.map((c) => c.field),
          },
        });
        if (gridSettings) dispatch(removeUserSettings([gridKey]));
    };

    return <> 
    <Box>
      <Grid item sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
        <Typography
          variant="body1"
          sx={{
            ...guideTitleStyles,
            fontWeight: 'bold',
          }}
        >
          Catalog
        </Typography>
        <Typography
          variant="body1"
          sx={{
            ...guideTitleStyles,

            ml: 1,
            mr: 1,
          }}
        >
          {' '}
          |{' '}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            ...guideTitleStyles,
          }}
        >
          {currentOrderGuide?.facility || currentOrderGuide?.templateName} | {currentOrderGuide?.agoraCategory}
        </Typography>
        <Grid item sx={{ display: 'flex', alignItems: 'center', ml: 2, mb: 0.5 }}>
          {!isSelectingCatalog ? (
            <Tooltip title="Select items to add to order guide">
              <Button variant="outlined" color="secondary" sx={{ maxWidth: 6 }} onClick={handleSelectClickCatalog}>
                Select
              </Button>
            </Tooltip>
          ) : (
            <>
              <Button variant="outlined" color="secondary" sx={{ mr: 1 }} onClick={handleAddSelectedRows}>
                Add to order guide
              </Button>
              <Button color="secondary" sx={{ maxWidth: 6 }} onClick={handleSelectClickCatalog}>
                Cancel
              </Button>
            </>
          )}
        </Grid>
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
          <CustomSettings
            columnsState={columnsState}
            setColumnsState={setColumnsState}
            gridColumns={inactiveColumns}
            resetGridState={resetGridState}
            handleGridSettingsUpdate={updateGridSettings}
          />
        </Box>
      </Grid>
    </Box>
    <DataGridPro
      initialState={initialState}
      apiRef={gridRef}
      getRowHeight={() => 52}
      rows={filteredRows}
      columns={inactiveColumns}
      disableSelectionOnClick
      loading={isLoading || isLoadingProducts}
      components={{
        LoadingOverlay: SkeletonDataGrid,
        NoRowsOverlay: () => (
          <Box sx={{ p: 2, textAlign: 'center', width: '100%' }}>
            <Typography variant="subtitle2" color="text.secondary">
              No rows
            </Typography>
          </Box>
        ),
      }}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={handleGridVisibilityChange}
      onColumnOrderChange={handleGridOrderChange}
      sortModel={sortModel}
      onSortModelChange={(model) => onSortModelChange(setSortModel, model, sortStorageKey)}
      filterModel={filterModel}
      onColumnWidthChange={setColumnWidths}
      disableColumnMenu
      disableColumnFilter
      disableColumnSelector
      disableColumnPinning
      checkboxSelection={isSelectingCatalog}
      rowSelectionModel={selectedRowIdsCatalog}
      disableRowSelectionOnClick
      onRowSelectionModelChange={handleSelectionModelChangeCatalog}
      componentsProps={{
        loadingOverlay: { height: 100}
      }}
      sx={{
        height: !filteredRows?.length ? 150 : 'auto',
        maxHeight: isCatalogExpanded ? 'calc(100vh - 150px)' : '33vh',
        width: '100%',
      }}
    />
    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
      {filteredRows.length > 0 && <Typography
        onClick={() => setIsCatalogExpanded(!isCatalogExpanded)}
        color="info.main"
        sx={{textDecoration: 'underline', cursor: 'pointer'}}
        variant="caption"
      >
        {isCatalogExpanded ? 'Collapse' : 'Expand'}
      </Typography>}
      <Typography variant="caption" color="text.secondary">
        Total: {filteredRows.length}
      </Typography>
    </Box>
    <CustomFilter
      gridId={gridId}
      anchorEl={anchorEl}
      setAnchorEl={setAnchorEl}
      setActiveFilter={setActiveFilter}
      activeFilter={activeFilter}
      filters={filters}
      setFilters={setFilters}
      fieldValues={fieldValues}
      columns={inactiveColumns}
    />
    <Dialog open={pendingRows.length > 0} onClose={() => !isAdding && setPendingRows([])} maxWidth="sm" fullWidth>
      <DialogTitle>Add Items to {guideCategoryName} Order Guide</DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 2 }}>
          The following items are not in the {guideCategoryName} category. They will be added to this order guide using the category and subcategory below unless you change them.
        </Alert>
        {pendingRows.filter(item => item.needsCategory).map(({ row, category, subcategory }) => {
          const isExistingCategory = category?.id === row.agoraCategory.id;
          return <Box key={row.id} sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {row.description}{row.productDetails[0]?.vendorItemId ? ` (${row.productDetails[0].vendorItemId})` : ''}
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Autocomplete
                  size="small"
                  disableClearable
                  options={[row.agoraCategory, { id: currentOrderGuide?.agoraCategoryId, name: guideCategoryName }]}
                  getOptionDisabled={(option) => option.id !== row.agoraCategory.id && !orderGuide.canAddCategory}
                  value={category}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
                  onChange={(event, value) => updateCategorySelection(row.id, {
                    category: value,
                    subcategory: value.id === row.agoraCategory.id ? row.agoraSubcategory : null,
                  })}
                  renderInput={(params) => <TextField {...params} label="Choose/Change Category" required />}
                />
              </Grid>
              <Grid item xs={6}>
                <Autocomplete
                  size="small"
                  disableClearable
                  options={isExistingCategory ? [row.agoraSubcategory] : subcategories}
                  disabled={isExistingCategory}
                  value={subcategory}
                  getOptionLabel={(option) => option?.name || ''}
                  isOptionEqualToValue={(option, selectedValue) => option.id === selectedValue.id}
                  onChange={(event, value) => updateCategorySelection(row.id, { subcategory: value })}
                  renderInput={(params) => <TextField {...params} label="Choose/Change Subcategory" required />}
                />
              </Grid>
            </Grid>
          </Box>;
        })}
      </DialogContent>
      <DialogActions>
        <Button disabled={isAdding} onClick={() => setPendingRows([])}>Cancel</Button>
        <Button variant="contained" disabled={!currentOrderGuide || isAdding || pendingRows.some(item => item.needsCategory && !item.subcategory)} onClick={handleConfirmAdd}>Add to order guide</Button>
      </DialogActions>
    </Dialog>
  </>
}
